import { RULE_SET_ID, SOURCE_REFS, makeRuleTrace } from "./sourceAuthority.js";
const SPARE_UFH_LIMIT_SENSOR_VALUE_ENGINEERING = {
    candidate: true,
    defaultIncluded: true,
    reason: "Default guideline spare UFH limit sensor; removable if not specified (value-engineering candidate)."
};
const SCREEN_DEVICE_PRODUCTS = {
    ts4: {
        productPartNo: "TP-UI-TS-4",
        family: "ui_screen",
        role: "local_ui_ts4",
        ruleTraceId: "trace-ts4-network-screen"
    },
    ts7: {
        productPartNo: "TP-UI-TS-7",
        family: "ui_screen",
        role: "central_control_screen_ts7",
        ruleTraceId: "trace-ts7-network-screen"
    },
    rdu: {
        productPartNo: "RDU-TOUCH/W",
        family: "ui_screen",
        role: "local_ui_rdu_touch",
        ruleTraceId: "trace-rdu-direct-wired"
    }
};
const SENSOR_DEVICE_PRODUCTS = {
    control_temperature: {
        productPartNo: "TPFMTS",
        family: "temperature_sensor",
        role: "control_temperature_sensor",
        ruleTraceId: "trace-control-temperature-sensor-device"
    },
    supply_air_off_coil: {
        productPartNo: "TPTVAV/S",
        family: "temperature_sensor",
        role: "supply_air_off_coil_sensor",
        ruleTraceId: "trace-supply-air-off-coil-sensor-device"
    },
    ufh_limit_active: {
        productPartNo: "TPTVAV/L",
        family: "temperature_sensor",
        role: "ufh_limit_sensor_active",
        ruleTraceId: "trace-ufh-limit-sensor-device"
    },
    ufh_limit_spare: {
        productPartNo: "TPTVAV/L",
        family: "temperature_sensor",
        role: "ufh_limit_sensor_spare",
        ruleTraceId: "trace-ufh-limit-sensor-device",
        valueEngineering: SPARE_UFH_LIMIT_SENSOR_VALUE_ENGINEERING
    }
};
export function getScreenDeviceProduct(kind) {
    return SCREEN_DEVICE_PRODUCTS[kind];
}
export function getSensorDeviceProduct(kind) {
    return SENSOR_DEVICE_PRODUCTS[kind];
}
export function getControlTemperatureSensorTrace() {
    return makeRuleTrace("trace-control-temperature-sensor-device", "rule-load-eufh-load-only", "Every control zone gets a dedicated control-temperature sensor; it must not be replaced by a UFH limit sensor.");
}
export function getSupplyAirOffCoilSensorTrace() {
    return makeRuleTrace("trace-supply-air-off-coil-sensor-device", "rule-supply-air-off-coil-sensor-default", "Supply-air/off-coil TPTVAV/S sensors default one per FCU zone for a higher-quality install and improved Edge anomaly detection.");
}
export function getUfhLimitSensorTrace() {
    return makeRuleTrace("trace-ufh-limit-sensor-device", "rule-sensor-spares-not-terminated", "UFH limit sensors are emitted as devices; spare and EUFH limit sensors must not add controller UI demand.");
}
export function deriveScreenMapping(request) {
    if (request.kind === "rdu") {
        if (!request.controllerId) {
            throw new Error("RDU mapping requires a controllerId for direct wiring.");
        }
        return {
            id: `screen-mapping-${request.id}`,
            kind: request.kind,
            mappingType: "direct_wired",
            switchPortDemand: 0,
            controllerId: request.controllerId,
            zoneId: request.zoneId,
            traces: [
                makeRuleTrace("trace-rdu-direct-wired", "rule-screen-rdu-direct-wired", "RDU is hard-wired 1:1 to its FCU controller and consumes no switch port.")
            ]
        };
    }
    const mapping = {
        id: `screen-mapping-${request.id}`,
        kind: request.kind,
        mappingType: "network_pairing",
        switchPortDemand: 1,
        zoneId: request.zoneId,
        traces: [
            makeRuleTrace(`trace-${request.kind}-network-screen`, "rule-screen-networked-ts4-ts7", `${request.kind.toUpperCase()} is a PoE/network screen and consumes one switch port.`)
        ]
    };
    if (request.controllerId) {
        mapping.controllerId = request.controllerId;
    }
    return mapping;
}
export function validateRduIsDirectWired(mapping) {
    const valid = mapping.kind !== "rdu" ||
        (mapping.mappingType === "direct_wired" &&
            mapping.switchPortDemand === 0 &&
            Boolean(mapping.controllerId));
    return {
        id: `constraint-rdu-direct-wired-${mapping.id}`,
        ruleSetId: RULE_SET_ID,
        constraintKey: "screen.rdu_direct_wired",
        status: valid ? "pass" : "fail",
        subjectRef: mapping.id,
        message: valid
            ? "RDU mapping is direct-wired to its controller and is not a network screen."
            : "RDU must be direct-wired 1:1 to a controller and must not consume a network switch port.",
        details: {
            kind: mapping.kind,
            mappingType: mapping.mappingType,
            switchPortDemand: mapping.switchPortDemand,
            controllerId: mapping.controllerId ?? null
        },
        sourceRefs: [SOURCE_REFS.baselineRules],
        ruleTraceRefs: mapping.traces.map((trace) => trace.id)
    };
}
export function deriveCentralScreenCounts(input) {
    if (input.uiStrategy === "central_only") {
        return {
            ts4Count: 0,
            ts7Count: 1,
            totalScreenCount: 1,
            replacementMode: "central_only",
            traces: [
                makeRuleTrace("trace-central-screen-controls-all-zones", "rule-screen-networked-ts4-ts7", "Central-only UI strategy derives one apartment-level TS-7/CCS and suppresses per-room TS-4/RDU screens; the screen is mapped to every controlled zone.")
            ]
        };
    }
    if (!input.centralScreenRequired) {
        return {
            ts4Count: input.thermostatCount,
            ts7Count: 0,
            totalScreenCount: input.thermostatCount,
            replacementMode: "none",
            traces: []
        };
    }
    if (input.countedThermostatAtCentralLocation && input.thermostatCount > 0) {
        return {
            ts4Count: input.thermostatCount - 1,
            ts7Count: 1,
            totalScreenCount: input.thermostatCount,
            replacementMode: "replaces_counted_thermostat",
            traces: [
                makeRuleTrace("trace-central-screen-replaces-thermostat", "rule-screen-networked-ts4-ts7", "Central TS-7 replaces exactly one counted thermostat when the central-screen location is known to be one of the counted thermostat points.")
            ]
        };
    }
    return {
        ts4Count: input.thermostatCount,
        ts7Count: 1,
        totalScreenCount: input.thermostatCount + 1,
        replacementMode: "additional",
        traces: [
            makeRuleTrace("trace-central-screen-additional", "rule-screen-networked-ts4-ts7", "Central TS-7 is additional because no counted thermostat can be replaced.")
        ]
    };
}
//# sourceMappingURL=screenRules.js.map